import type { Action, Thunk, Computed, ThunkOn } from 'easy-peasy';
import { action, thunk, computed, thunkOn } from 'easy-peasy';
import type { ApplicationErrorOptions } from '../../types';
import { queryOptions } from './utils';
import type {
  UILabeledAggregation,
  AggregateReportOptions,
  CreateManifestHistoryReportResponse,
  CreateManifestHistoryReportRequest,
  DownloadManifestHistoryReportRequest,
} from '../services/SiteReportingService/types';
import type { Injections } from '../services/injections';
import type { OrderToDeliverReportSummaryDetail } from './orderToDeliverReportComputedFields';
import { orderToDeliverReportSummary } from './orderToDeliverReportComputedFields';
import type {
  DeliveryPerformanceReportSummaryDetail,
  DeliveryPerformanceReportLineData,
} from './deliveryPerformaceComputed';
import { deliveryPerfomanceLineData, deliveryPerfomanceSummary } from './deliveryPerformaceComputed';
import type { ServiceLevelReportSummaryDetail, ServiceLevelReportLineData } from './serviceLevelReportComputedFields';
import { serviceLevelReportSummary, serviceLevelLineData } from './serviceLevelReportComputedFields';
import type { StoreModel } from '..';
import { store } from '..';
import { subDays } from 'date-fns';
import { replace } from 'redux-first-history';
import FileSaver from 'file-saver';
import parseStatusCode from '../errors/parseStatusCode';

export interface SiteReportingModel {
  lastAction?: number;
  setUseDateRange: Action<SiteReportingModel, boolean>;
  useCustomDateRange?: boolean;
  useCustomPercentileOption?: boolean;
  setUseCustomPercentileOption: Action<SiteReportingModel, boolean>;
  orderToDeliverReport: UILabeledAggregation[];
  serviceLevelReport: UILabeledAggregation[];
  deliveryPerformanceOptions: AggregateReportOptions;
  orderToDeliverOptions: AggregateReportOptions;
  serviceLevelReportOptions: AggregateReportOptions;
  updateOrderToDeliverReportOptions: Action<SiteReportingModel, AggregateReportOptions>;
  onUpdateOrderToDeliverOptions: ThunkOn<SiteReportingModel, void, StoreModel>;
  onReportingOptionsChange: ThunkOn<SiteReportingModel, void, StoreModel>;
  onUpdateServiceLevelReportOptions: ThunkOn<SiteReportingModel, void, StoreModel>;
  updateServiceLevelReportOptions: Action<SiteReportingModel, AggregateReportOptions>;
  siteReportActionStart: Action<SiteReportingModel, void>;
  siteReportActionFailure: Action<SiteReportingModel, ApplicationErrorOptions>;
  fetchServiceLevelReport: Thunk<
    SiteReportingModel,
    AggregateReportOptions,
    Injections,
    StoreModel,
    Promise<UILabeledAggregation[] | undefined>
  >;
  fetchOrderToDeliverReport: Thunk<
    SiteReportingModel,
    AggregateReportOptions,
    Injections,
    StoreModel,
    Promise<UILabeledAggregation[] | undefined>
  >;
  setOrderToDeliverReport: Action<SiteReportingModel, UILabeledAggregation[]>;
  setServiceLevelReport: Action<SiteReportingModel, UILabeledAggregation[]>;
  deliveryPerformanceSummary: Computed<SiteReportingModel, DeliveryPerformanceReportSummaryDetail>;
  deliveryPerfomanceLineData: Computed<SiteReportingModel, DeliveryPerformanceReportLineData[]>;
  updateDeliveryPerformanceOptions: Action<SiteReportingModel, AggregateReportOptions>;
  deliveryPerformanceReport: UILabeledAggregation[];
  setDeliveryPerformance: Action<SiteReportingModel, UILabeledAggregation[]>;
  onUpdateDeliveryPerfromanceReportOptions: ThunkOn<SiteReportingModel, void, StoreModel>;
  orderToDeliverSummary: Computed<SiteReportingModel, OrderToDeliverReportSummaryDetail[]>;
  serviceLevelReportSummary: Computed<SiteReportingModel, (slaDays: number) => ServiceLevelReportSummaryDetail>;
  serviceLevelReportLineData: Computed<SiteReportingModel, (slaDays: number) => ServiceLevelReportLineData[]>;
  fetchDeliveryPerformanceReport: Thunk<
    SiteReportingModel,
    AggregateReportOptions,
    Injections,
    StoreModel,
    Promise<UILabeledAggregation[] | undefined>
  >;
  showCarrierOptions: boolean;
  setShowCarrierOptions: Action<SiteReportingModel, boolean>;
  createManifestReportLoading: boolean;
  setCreateManifestReportLoading: Action<SiteReportingModel, boolean>;
  createManifestReportHistoryRequest: Thunk<
    SiteReportingModel,
    CreateManifestHistoryReportRequest,
    Injections,
    StoreModel,
    Promise<CreateManifestHistoryReportResponse>
  >;
  downloadManifestReportLoading: Record<string, boolean>;
  setDownloadManifestReportLoading: Action<SiteReportingModel, Record<string, boolean>>;
  downloadManifestHistoryReport: Thunk<
    SiteReportingModel,
    DownloadManifestHistoryReportRequest,
    Injections,
    StoreModel,
    Promise<any>
  >;
}

export const siteReportingModel: SiteReportingModel = {
  useCustomDateRange: false,
  useCustomPercentileOption: false,
  setUseCustomPercentileOption: action((state, payload) => {
    state.useCustomPercentileOption = payload;
  }),
  setUseDateRange: action((state, payload) => {
    state.useCustomDateRange = payload;
  }),
  deliveryPerformanceOptions: {
    startDate: queryOptions.startDate || subDays(Date.now(), 7).valueOf(),
    endDate: queryOptions.endDate || Date.now(),
    timeDivider: queryOptions.timeDivider || 'Day',
    serviceLevelReportingType: 'otsDaysCount',
  },
  orderToDeliverOptions: {
    startDate: queryOptions.startDate || subDays(Date.now(), 7).valueOf(),
    endDate: queryOptions.endDate || Date.now(),
    timeDivider: queryOptions.timeDivider || 'Day',
    serviceLevelReportingType: 'otdDaysPercentile',
  },
  serviceLevelReportOptions: {
    startDate: queryOptions.startDate || subDays(Date.now(), 7).valueOf(),
    endDate: queryOptions.endDate || Date.now(),
    timeDivider: queryOptions.timeDivider || 'Day',
    serviceLevelReportingType: queryOptions.serviceLevelReportingType || 'otsDaysCount',
  },
  updateDeliveryPerformanceOptions: action((state, payload) => {
    state.deliveryPerformanceOptions = payload;
  }),
  updateOrderToDeliverReportOptions: action((state, payload) => {
    state.orderToDeliverOptions = payload;
  }),
  updateServiceLevelReportOptions: action((state, payload) => {
    state.serviceLevelReportOptions = payload;
  }),
  onUpdateOrderToDeliverOptions: thunkOn(
    (actions, storeActions) => actions.updateOrderToDeliverReportOptions,
    (actions, payload) => {
      actions.fetchOrderToDeliverReport(payload.payload);
    },
  ),
  onUpdateServiceLevelReportOptions: thunkOn(
    (actions, storeActions) => actions.updateServiceLevelReportOptions,
    (actions, payload) => {
      actions.fetchServiceLevelReport(payload.payload);
    },
  ),
  onUpdateDeliveryPerfromanceReportOptions: thunkOn(
    (actions, storeActions) => actions.updateDeliveryPerformanceOptions,
    (actions, payload) => {
      actions.fetchDeliveryPerformanceReport(payload.payload);
    },
  ),
  onReportingOptionsChange: thunkOn(
    (actions, storeActions) => [actions.updateServiceLevelReportOptions, actions.updateOrderToDeliverReportOptions],
    (actions, payload) => {
      const { startDate, endDate, timeDivider, serviceLevelReportingType } = payload.payload;
      let url = `${window.location.pathname}?startDate=${startDate}&endDate=${endDate}&timeDivider=${timeDivider}`;
      if (serviceLevelReportingType) {
        url = `${url}&serviceLevelReportingType=${serviceLevelReportingType}`;
      }
      store.dispatch(replace(url));
    },
  ),
  siteReportActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  siteReportActionFailure: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  deliveryPerfomanceLineData: computed((state) => {
    return deliveryPerfomanceLineData(state.deliveryPerformanceReport);
  }),
  deliveryPerformanceSummary: computed((state) => {
    return deliveryPerfomanceSummary(state.deliveryPerformanceReport);
  }),
  deliveryPerformanceReport: [],
  setDeliveryPerformance: action((state, payload) => {
    state.deliveryPerformanceReport = payload;
  }),
  orderToDeliverSummary: computed((state) => {
    return orderToDeliverReportSummary(state.orderToDeliverReport);
  }),
  serviceLevelReportSummary: computed((state) => (slaDays: number) => {
    return serviceLevelReportSummary(state.serviceLevelReport, slaDays);
  }),
  serviceLevelReportLineData: computed((state) => (slaDays: number) => {
    return serviceLevelLineData(state.serviceLevelReport, slaDays);
  }),
  fetchDeliveryPerformanceReport: thunk(async (actions, payload, { injections }) => {
    try {
      const { siteReportingService } = injections;
      actions.siteReportActionStart();
      const response = await siteReportingService.fetchAggregateReport(payload);
      actions.setDeliveryPerformance(response);
      return response;
    } catch (error) {
      actions.setDeliveryPerformance([]);
      actions.siteReportActionFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message,
      });
    }
  }),
  orderToDeliverReport: [],
  fetchOrderToDeliverReport: thunk(async (actions, payload, { injections }) => {
    try {
      actions.siteReportActionStart();
      const { siteReportingService } = injections;
      const response = await siteReportingService.fetchAggregateReport(payload);
      actions.setOrderToDeliverReport(response);
      return response;
    } catch (error) {
      actions.setOrderToDeliverReport([]);
      actions.siteReportActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
    }
  }),
  setOrderToDeliverReport: action((state, payload) => {
    state.orderToDeliverReport = payload;
  }),

  serviceLevelReport: [],
  fetchServiceLevelReport: thunk(async (actions, payload, { injections }) => {
    try {
      actions.siteReportActionStart();
      const { siteReportingService } = injections;
      const response = await siteReportingService.fetchAggregateReport(payload);
      actions.setServiceLevelReport(response);
      return response;
    } catch (error) {
      actions.setServiceLevelReport([]);
      actions.siteReportActionFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message,
      });
      return error;
    }
  }),
  setServiceLevelReport: action((state, payload) => {
    state.serviceLevelReport = payload;
  }),
  showCarrierOptions: false,
  setShowCarrierOptions: action((state, payload) => {
    state.showCarrierOptions = payload;
  }),
  createManifestReportLoading: false,
  setCreateManifestReportLoading: action((state, payload) => {
    state.createManifestReportLoading = payload;
  }),
  createManifestReportHistoryRequest: thunk(async (actions, payload, { injections }) => {
    try {
      actions.setCreateManifestReportLoading(true);
      const { siteReportingService } = injections;
      const response = await siteReportingService.createManifestHistoryReport(payload);
      actions.setCreateManifestReportLoading(false);

      return response;
    } catch (error) {
      actions.setCreateManifestReportLoading(false);
      actions.siteReportActionFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message,
      });
      return error;
    }
  }),
  downloadManifestReportLoading: {},
  setDownloadManifestReportLoading: action((state, payload) => {
    state.downloadManifestReportLoading = {
      ...state.downloadManifestReportLoading,
      ...payload,
    };
  }),
  downloadManifestHistoryReport: thunk(async (actions, payload, { injections }) => {
    try {
      actions.setDownloadManifestReportLoading({ [payload.manifestId]: true });
      const { siteReportingService } = injections;
      const result = await siteReportingService.downloadManifestHistoryReport(payload);
      result?.manifestReportList?.forEach((entry, index) => {
        const buffer = Buffer.from(entry.manifestData.toString(), 'base64');
        const blobData = new Blob([buffer], {
          type: entry.manifestFormat.toLowerCase() === 'pdf' ? 'application/pdf' : 'text/plain',
        });
        FileSaver.saveAs(
          blobData,
          `${payload.manifestId}_${index}_${new Date().toISOString()}${entry.manifestFormat.toLowerCase() === 'pdf' ? '.pdf' : '.zpl'}`,
        );
      });
      actions.setDownloadManifestReportLoading({ [payload.manifestId]: false });
    } catch (error) {
      actions.setDownloadManifestReportLoading({ [payload.manifestId]: false });
      actions.siteReportActionFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: true,
        message: error.message,
      });
      return error;
    }
  }),
};
